import React from 'react';

import classNames from 'classnames';

import { useSearchParams } from 'react-router-dom';

import { createLocale } from 'locales';

import Typography from 'components-new/typography/typography';

import useGetPeriodJobDetails from 'state/remote/remote-organization/use-get-period-job-details';

import Button from 'components-new/button/button';

import EmptyState from 'components-new/empty-state/empty-state';

import LoadingPage from 'pages/LoadingPage';

import PeriodJobDetails from './PeriodJobDetails';
import RatingStars from './RatingStars';

interface PeriodJobDetailsViewProps {
  jobId: number;
}
const localize = createLocale('organisationPeriodJobsLocales');

const formatRate = (rate: number, vatExempt: boolean) => {
  if (vatExempt) return `€ ${rate} (btw vrij) `;
  return `€ ${rate} (excl) / € ${(rate * 1.21).toFixed(2)} (incl)`;
};
const boxShadow = '0px 2px 5px 0px rgba(25, 23, 26, 0.06)';
const PeriodJobDetailsView: React.FC<PeriodJobDetailsViewProps> = ({
  jobId,
}) => {
  const { periodJob, isLoading, error } = useGetPeriodJobDetails(jobId);
  const setSearchParams = useSearchParams()[1];

  if (isLoading || !periodJob) return <LoadingPage />;

  if (error) return <EmptyState errorText={error} />;
  return (
    <div className="w-full md:w-full my-5 mx-6 md:mx-3">
      <PeriodJobDetails periodJob={periodJob} title="Opdracht informatie" />
      <div className="mt-6">
        <Typography as="h2" variant="heading-1" className="text-[#43344D]">
          {localize('freelancers')}
        </Typography>

        <Typography
          as="p"
          variant="body-medium"
          className="text-[#545255] mt-2 mb-3"
        >
          {classNames(
            !!periodJob.interested_freelancers?.length &&
              localize('freelancersListHelpText'),
          )}
        </Typography>
      </div>
      <div className="mt-10 grid gap-y-4 gap-x-1 md:gap-x-5 lg:gap-x-2 grid-cols-1 md:grid-cols-2 xl:grid-cols-3">
        {periodJob.interested_freelancers?.map((freelancer) => {
          return (
            <div
              style={{ boxShadow }}
              key={freelancer.freelancer_id}
              className="mt-3 px-3 md:px-1 lg:px-3 pt-3  border border-solid border-[#E7E6E8] rounded-md flex flex-col items-start justify-start shadow-6"
            >
              <div className="flex items-start justify-start">
                <img
                  src={freelancer.picture}
                  alt={freelancer.name}
                  className="h-[140px] w-[140px] object-cover border-1/2 rounded-full bg-contain text-caption flex items-center justify-center text-center"
                />
                <div className="flex flex-col items-start justify-start ml-3">
                  <Typography
                    as="h4"
                    variant="body-large-bold"
                    className="mt-4"
                  >
                    {freelancer.name}
                  </Typography>
                  <RatingStars
                    maxRating={5}
                    rating={freelancer.avg_rating || 0}
                  />
                  <Typography as="h4" variant="body-large" className="mt-2">
                    {formatRate(
                      parseInt(freelancer.rate, 10),
                      freelancer.vat_exempt,
                    )}
                  </Typography>
                </div>
              </div>
              <div className="mt-4 flex justify-between items-center mb-6 w-full">
                <Button
                  label={localize('freelancerProfile')}
                  variant="secondary"
                  onClick={() => {
                    window.location.assign(
                      `/organisation/freelancers/${freelancer.freelancer_id}/profile/`,
                    );
                  }}
                />
                <Button
                  label={localize('confirm')}
                  variant="primary"
                  disabled={periodJob.is_confirmed}
                  onClick={() => {
                    setSearchParams({
                      confirm: 'true',
                      freelancer_id: freelancer.freelancer_id.toString(),
                      job_id: jobId.toString(),
                    });
                  }}
                />
              </div>
            </div>
          );
        })}
        {!periodJob.interested_freelancers?.length && (
          <div className="col-span-full flex items-center  justify-center">
            <EmptyState
              variant="colorful"
              errorText={localize('noInterestedFreelancers')}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default PeriodJobDetailsView;
